import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import HistoryEduRoundedIcon from '@mui/icons-material/HistoryEduRounded';
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded';

import { Section, SectionHeading, Panel, Reveal, Disclosure } from '../components/ui';
import { SERVICE_KEYS } from '../data/services';
import { RADIUS, TYPE } from '../theme/tokens';

// Cheia e și ancora din URL (#contract), deci linkurile din capul paginii sunt
// aceleași cu identificatorii secțiunilor. `index.css` dă tuturor elementelor
// cu `id` o `scroll-margin-top`, ca titlul să nu aterizeze sub bara fixă.
//
// Lista vine din `data/services`, fiindcă o folosește și formularul de contact;
// aici se adaugă doar semnul fiecărei direcții.
const ICONS = {
  contract: <TimelineRoundedIcon />,
  quality: <ChecklistRoundedIcon />,
  archaeology: <HistoryEduRoundedIcon />,
  lowCurrent: <SensorsRoundedIcon />,
};

const SERVICES = SERVICE_KEYS.map((key) => ({ key, icon: ICONS[key] }));

export default function Services() {
  const { t } = useTranslation();

  const examplesOf = (key) => {
    const value = t(`services.${key}.examples`, { returnObjects: true });
    return Array.isArray(value) ? value : [];
  };

  return (
    <>
      {/* ────────────────────────────  Capul  ───────────────────────────── */}
      <Section first component="header">
        <SectionHeading
          component="h1"
          align="center"
          eyebrow={t('services.eyebrow')}
          title={t('services.title')}
          lede={t('services.subtitle')}
          titleSx={TYPE.h1}
        />

        {/* Scurtătura către fiecare direcție. Pe telefon rândul se derulează
            lateral în loc să se rupă în trei rânduri de pastile. */}
        <Reveal
          delay={0.12}
          sx={{
            display: 'flex',
            gap: 1,
            mt: { xs: 4, md: 5 },
            justifyContent: { xs: 'flex-start', md: 'center' },
            overflowX: 'auto',
            // Pastilele nu se lipesc de marginea ecranului la capătul derulării.
            paddingInline: { xs: 0.5, md: 0 },
            mx: { xs: -0.5, md: 0 },
            scrollSnapType: 'x proximity',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {SERVICES.map((service) => (
            <Box
              key={service.key}
              component="a"
              href={`#${service.key}`}
              sx={{
                flex: 'none',
                scrollSnapAlign: 'start',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.85,
                minHeight: 42,
                px: 2,
                borderRadius: `${RADIUS.pill}px`,
                border: '1px solid var(--qf-line)',
                backgroundColor: 'var(--qf-card)',
                color: 'var(--qf-ink-2)',
                textDecoration: 'none',
                ...TYPE.small,
                fontWeight: 500,
                whiteSpace: 'nowrap',
                '@media (hover: hover)': {
                  '&:hover': { color: 'var(--qf-ink)', borderColor: 'var(--qf-line-2)' },
                },
                '&:focus-visible': { outline: '2px solid var(--qf-accent)', outlineOffset: 2 },
                '& svg': { fontSize: 18, color: 'var(--qf-accent)' },
              }}
            >
              {service.icon}
              {t(`services.${service.key}.title`)}
            </Box>
          ))}
        </Reveal>
      </Section>

      {/* ───────────────────────  Cele patru direcții  ──────────────────── */}
      {SERVICES.map((service, i) => (
        <Section key={service.key} id={service.key} tone={i % 2 === 0 ? 'alt' : 'page'}>
          <Box sx={{ maxWidth: 880, mx: 'auto' }}>
            <Reveal>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 2, sm: 2.5 }, mb: { xs: 3, md: 4 } }}>
                <Box
                  aria-hidden
                  sx={{
                    display: 'grid',
                    placeItems: 'center',
                    flex: 'none',
                    width: { xs: 48, md: 56 },
                    height: { xs: 48, md: 56 },
                    borderRadius: '16px',
                    backgroundColor: 'var(--qf-accent-soft)',
                    color: 'var(--qf-accent)',
                    '& svg': { fontSize: { xs: 24, md: 28 } },
                  }}
                >
                  {service.icon}
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography component="h2" sx={{ ...TYPE.h2, fontSize: 'clamp(1.5rem, 3.4vw, 2.125rem)', m: 0 }}>
                    {t(`services.${service.key}.title`)}
                  </Typography>
                  <Typography sx={{ ...TYPE.body, color: 'var(--qf-ink-2)', mt: 1.25, textWrap: 'pretty' }}>
                    {t(`services.${service.key}.description`)}
                  </Typography>
                </Box>
              </Box>
            </Reveal>

            <Reveal delay={0.08}>
              <Panel bodySx={{ px: { xs: 2.5, md: 3.5 } }}>
                <Disclosure items={examplesOf(service.key)} />
              </Panel>
            </Reveal>
          </Box>
        </Section>
      ))}
    </>
  );
}
